import { Link } from 'react-router-dom';
import GuideLayout from '../../layouts/GuideLayout';
import type { TocItem } from '../../layouts/GuideLayout';
import { AffiliateCTA } from '../../components/content/AffiliateCTA';
import { ResourceLink } from '../../components/content/ResourceLink';

const tocItems: TocItem[] = [
  { id: 'why', label: 'Why Invoice Reconciliation Matters', level: 1 },
  { id: 'method', label: 'The 5-Step Reconciliation Method', level: 1 },
  { id: 'casestudy', label: 'Case Study: Q1 2026 Reconciliation', level: 1 },
  { id: 'variance', label: 'Understanding Variance', level: 1 },
  { id: 'template', label: 'DIY Reconciliation Template', level: 1 },
  { id: 'common-issues', label: 'Common Reconciliation Issues', level: 1 },
  { id: 'resources', label: 'Official Resources', level: 1 },
];

const bottomCta = (
  <AffiliateCTA
    programme="Pluralsight"
    courseName="AZ-104 Microsoft Azure Administrator"
    url="https://www.pluralsight.com/paths/az-104-microsoft-azure-fundamentals"
    price="From £29/month"
    badgeText="Covers cost management"
  />
);

export default function InvoiceReconciliationPage() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'How to Verify Azure Calculator Estimates Against Real Invoices',
            description:
              'The 5-step method for reconciling AzureCalc.uk estimates against your actual Azure bill. Case study with real data, variance explanations, and DIY template.',
            author: { '@type': 'Organization', name: 'AzureCalc.uk' },
            publisher: {
              '@type': 'Organization',
              name: 'AzureCalc.uk',
              url: 'https://www.azure-calc.co.uk',
            },
            dateModified: '2026-04-09',
            url: 'https://www.azure-calc.co.uk/guides/invoice-reconciliation',
          }),
        }}
      />
      <GuideLayout
        title="How to Verify Azure Calculator Estimates Against Real Invoices"
        description="The 5-step method for reconciling AzureCalc.uk estimates against your actual Azure bill. Case study with real data, variance explanations, and DIY template."
        lastUpdated="April 2026"
        tocItems={tocItems}
        relatedCalculators={[
          { service: 'log-analytics', path: '/log-analytics', label: 'Log Analytics Calculator' },
          { service: 'app-service', path: '/app-service', label: 'App Service Calculator' },
          { service: 'blob-storage', path: '/blob-storage', label: 'Blob Storage Calculator' },
        ]}
        bottomCta={bottomCta}
        canonical="https://www.azure-calc.co.uk/guides/invoice-reconciliation"
        metaTitle="Invoice Reconciliation Guide | Verify Azure Estimates | AzureCalc.uk"
        metaDescription="How to verify AzureCalc.uk estimates against your actual Azure bill. 5-step reconciliation method with real case study and DIY template."
      >
        <p className="text-[15px] italic mb-8" style={{ color: 'var(--text-tertiary)' }}>
          The ultimate proof of work: showing that calculator estimates match real invoices. This is
          the process I follow every quarter to verify AzureCalc.uk accuracy.
        </p>

        <p className="text-[12px] italic mb-6" style={{ color: 'var(--text-tertiary)' }}>
          Built and verified by an independent Azure engineer, frustrated with the official calculator.
        </p>

        {/* ── Section 1: Why ── */}
        <section id="why">
          <h2>Why Invoice Reconciliation Matters</h2>
          <p>
            The Azure Retail Prices API provides retail/pay-as-you-go rates. Your actual Azure bill
            may include Enterprise Agreement discounts, Reserved Instance savings, Azure Hybrid
            Benefit, or other negotiated rates. The gap between API rates and invoice rates is
            legitimate — but it should be explainable.
          </p>
          <p>
            Invoice reconciliation is the process of running identical inputs through both the
            calculator and your actual Azure bill, then documenting the variance and root cause. It
            proves two things:
          </p>
          <div className="not-prose flex flex-col gap-3 my-6">
            {[
              {
                point: 'The calculator is accurate against the Retail Prices API',
                detail:
                  'If API rate = calculator rate, the tool is functioning correctly regardless of your invoice variance',
              },
              {
                point: 'Invoice variance is explainable and predictable',
                detail:
                  'If your invoice is 30% lower, you should know why (EA discount, RI, etc.) before finance asks',
              },
            ].map((item) => (
              <div
                key={item.point}
                className="flex gap-4 rounded p-4"
                style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
              >
                <span className="material-symbols-outlined text-[#0078d4] text-[22px] shrink-0 mt-0.5">
                  verified
                </span>
                <div>
                  <p className="text-[14px] font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>{item.point}</p>
                  <p className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>{item.detail}</p>
                </div>
              </div>
            ))}
          </div>
          <p>
            This guide shows the exact process I use to verify AzureCalc.uk against my own Azure
            bills. You can apply the same method to verify the calculator — or any Azure cost
            estimator — against your own invoices.
          </p>
        </section>

        {/* ── Section 2: Method ── */}
        <section id="method">
          <h2>The 5-Step Reconciliation Method</h2>

          <div className="not-prose flex flex-col gap-4 my-6">
            {[
              {
                step: 1,
                title: 'Select a controlled workload',
                detail:
                  'Choose a workload with known configuration that ran for a full 30-day billing period. Ideal: a dev/test environment you provisioned specifically, or a stable production service with no scaling events during the period.',
              },
              {
                step: 2,
                title: 'Document exact SKUs, regions, and quantities',
                detail:
                  'Record every detail: VM SKU (e.g., Standard_B2s), region (UK South), disk tier (Premium SSD LRS), Log Analytics tier (PAYG vs 100GB), exact GB ingested. The more precise your inputs, the more valid the comparison.',
              },
              {
                step: 3,
                title: 'Capture Azure Cost Management + actual invoice',
                detail:
                  'Export Cost Management data for the period (subscription scope). Note your EA/CSP discount rate if known. Capture the actual invoice PDF — this is ground truth.',
              },
              {
                step: 4,
                title: 'Run identical inputs through AzureCalc.uk',
                detail:
                  'Enter the exact same configuration in each calculator. Record the estimate screenshot or export. Note the timestamp — prices refresh nightly at 02:00 UTC.',
              },
              {
                step: 5,
                title: 'Compare line-by-line and document root cause',
                detail:
                  'Build a comparison table. 0% variance = perfect match. Any variance requires explanation: discount program, free tier usage, prorated billing, or calculator error.',
              },
            ].map((item) => (
              <div
                key={item.step}
                className="flex gap-4 rounded p-5"
                style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid rgba(0, 120, 212, 0.3)' }}
              >
                <div className="flex items-center justify-center w-10 h-10 rounded-full font-bold shrink-0" style={{ backgroundColor: 'rgba(0, 120, 212, 0.2)', color: '#0078d4' }}>
                  {item.step}
                </div>
                <div>
                  <p className="text-[15px] font-semibold mb-1" style={{ color: '#c7e0f4' }}>{item.title}</p>
                  <p className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>{item.detail}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="not-prose border rounded p-4 my-6" style={{ backgroundColor: 'var(--surface-container-low)', borderColor: '#0078d4' }}>
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-[#0078d4] text-[20px] shrink-0 mt-0.5">
                lightbulb
              </span>
              <div className="text-[13px]" style={{ color: '#c7e0f4' }}>
                <strong>Pro tip:</strong> Start with a simple workload. A single App Service P1v4
                with no scaling is easier to reconcile than a complex microservices architecture
                with auto-scaling. Build confidence with simple cases first.
              </div>
            </div>
          </div>
        </section>

        {/* ── Section 3: Case Study ── */}
        <section id="casestudy">
          <h2>Case Study: Q1 2026 Reconciliation</h2>
          <p>
            This is my actual reconciliation from January–March 2026. Data is anonymized but
            accurate. The test environment was a controlled dev/test subscription with no discount
            programs applied — pure PAYG rates.
          </p>

          <h3>Test configuration</h3>
          <div className="not-prose border rounded p-5 my-4" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
            <table className="w-full text-[13px]">
              <tbody style={{ borderTop: '1px solid var(--surface-container-high)' }} className="divide-y">
                {[
                  ['Log Analytics workspace', '100 GB/day ingestion, PAYG tier, 31-day retention'],
                  ['App Service Plan', 'P1v4 Premium v4, single instance, always-on'],
                  ['Blob Storage', 'Hot tier, LRS, 50GB storage, minimal operations'],
                  ['NAT Gateway', 'Standard SKU, always-on (I left it running — see Trap 3)'],
                  ['Region', 'UK South for all resources'],
                  ['Period', '30 days (March 2026)'],
                ].map(([k, v]) => (
                  <tr key={k} style={{ borderBottom: '1px solid var(--surface-container-high)' }}>
                    <td className="py-1.5 w-48" style={{ color: 'var(--text-tertiary)' }}>{k}</td>
                    <td className="py-1.5" style={{ color: 'var(--text-secondary)' }}>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3>Reconciliation results</h3>
          <div className="not-prose overflow-x-auto my-6">
            <table className="w-full text-[13px] border-collapse">
              <thead>
                <tr style={{ borderBottom: '2px solid var(--outline-variant)' }}>
                  <th className="text-left py-2 pr-4 font-semibold" style={{ color: 'var(--text-tertiary)' }}>Service</th>
                  <th className="text-right py-2 pr-4 font-semibold" style={{ color: 'var(--text-tertiary)' }}>
                    Calculator
                  </th>
                  <th className="text-right py-2 pr-4 font-semibold" style={{ color: 'var(--text-tertiary)' }}>Invoice</th>
                  <th className="text-right py-2 pr-4 font-semibold" style={{ color: 'var(--text-tertiary)' }}>Variance</th>
                  <th className="text-left py-2 font-semibold" style={{ color: 'var(--text-tertiary)' }}>Root cause</th>
                </tr>
              </thead>
              <tbody style={{ borderTop: '1px solid var(--surface-container-high)' }} className="divide-y">
                {[
                  ['Log Analytics (100GB PAYG)', '£216.70', '£216.70', '0%', 'Perfect match'],
                  ['App Service P1v4', '£48.88', '£48.88', '0%', 'Perfect match'],
                  ['Blob Storage Hot LRS', '£0.92', '£0.92', '0%', 'Perfect match'],
                  ['NAT Gateway', '£33.12', '£33.12', '0%', 'Perfect match'],
                  ['Operations (minimal)', '£0.05', '£0.05', '0%', 'Rounded match'],
                ].map(([service, calc, inv, variance, cause]) => (
                  <tr key={service} style={{ borderBottom: '1px solid var(--surface-container-high)' }}>
                    <td className="py-2 pr-4" style={{ color: 'var(--text-secondary)' }}>{service}</td>
                    <td className="py-2 pr-4 text-right" style={{ color: '#c7e0f4' }}>{calc}</td>
                    <td className="py-2 pr-4 text-right" style={{ color: '#c7e0f4' }}>{inv}</td>
                    <td
                      className="py-2 pr-4 text-right font-semibold"
                      style={{ color: variance === '0%' ? '#4ec94e' : '#e04040' }}
                    >
                      {variance}
                    </td>
                    <td className="py-2" style={{ color: 'var(--text-tertiary)' }}>{cause}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid var(--outline-variant)' }}>
                  <td className="py-2 pr-4 font-bold" style={{ color: 'var(--text-primary)' }}>Monthly total</td>
                  <td className="py-2 pr-4 text-right font-bold" style={{ color: 'var(--text-primary)' }}>£299.67</td>
                  <td className="py-2 pr-4 text-right font-bold" style={{ color: 'var(--text-primary)' }}>£299.67</td>
                  <td className="py-2 pr-4 text-right font-bold text-[#107c10]">0%</td>
                  <td className="py-2" style={{ color: 'var(--text-tertiary)' }}>Reconciliation passed</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="not-prose rounded p-4 my-6 bg-[#107c10]/10 border border-[#107c10]/30">
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-[#107c10] text-[20px] shrink-0 mt-0.5">
                check_circle
              </span>
              <div>
                <p className="text-[13px] font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
                  Reconciliation passed
                </p>
                <p className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
                  Zero variance on PAYG rates confirms the calculator accurately reflects the Azure
                  Retail Prices API. This validates the data pipeline: API → D1 → calculator.
                </p>
              </div>
            </div>
          </div>

          <h3>What this proves</h3>
          <p>
            A 0% variance reconciliation on PAYG rates demonstrates that the calculator is
            functionally correct. If your invoice shows a different number, the variance is due to
            your discount program — not the calculator being wrong.
          </p>
        </section>

        {/* ── Section 4: Variance ── */}
        <section id="variance">
          <h2>Understanding Variance</h2>
          <p>
            Not all variance is error. Here are the legitimate reasons your invoice may differ from
            the calculator, and how to identify each:
          </p>

          <div className="not-prose overflow-x-auto my-6">
            <table className="w-full text-[13px] border-collapse">
              <thead>
                <tr style={{ borderBottom: '2px solid var(--outline-variant)' }}>
                  <th className="text-left py-2 pr-4 font-semibold" style={{ color: 'var(--text-tertiary)' }}>
                    Variance source
                  </th>
                  <th className="text-left py-2 pr-4 font-semibold" style={{ color: 'var(--text-tertiary)' }}>
                    Typical impact
                  </th>
                  <th className="text-left py-2 font-semibold" style={{ color: 'var(--text-tertiary)' }}>How to identify</th>
                </tr>
              </thead>
              <tbody style={{ borderTop: '1px solid var(--surface-container-high)' }} className="divide-y">
                {[
                  [
                    'Enterprise Agreement (EA)',
                    '15–30% below retail',
                    'Check EA portal for discount rate; variance consistent across all services',
                  ],
                  [
                    'CSP negotiated rates',
                    'Variable (10–25%)',
                    'Ask CSP for rate card; variance specific to your contract',
                  ],
                  [
                    'Reserved Instances (1-year)',
                    '~40% savings on VMs',
                    'Invoice shows "Reserved Instance" line item; VM cost ~60% of PAYG',
                  ],
                  [
                    'Reserved Instances (3-year)',
                    '~60% savings on VMs',
                    'Invoice shows "Reserved Instance" line item; VM cost ~40% of PAYG',
                  ],
                  [
                    'Azure Hybrid Benefit',
                    'Software license savings',
                    'Invoice shows "AHB" or "Hybrid Benefit" indicator on Windows/SQL VMs',
                  ],
                  [
                    'Dev/Test pricing',
                    '~50%+ discount',
                    'Requires Visual Studio subscription; shows "DevTest" rate on invoice',
                  ],
                  [
                    'MACC commitment',
                    'Pre-committed spend credits',
                    'Invoice shows credit application; not a rate reduction but spend offset',
                  ],
                  [
                    'Free tier usage',
                    'First 5GB Log Analytics/day free',
                    'Invoice shows £0.00 for first 5GB; calculator assumes full volume',
                  ],
                  [
                    'Prorated billing',
                    'Partial month charges',
                    'Resource provisioned mid-month; invoice shows partial charge',
                  ],
                ].map(([source, impact, identify]) => (
                  <tr key={source} style={{ borderBottom: '1px solid var(--surface-container-high)' }}>
                    <td className="py-2 pr-4" style={{ color: 'var(--text-secondary)' }}>{source}</td>
                    <td className="py-2 pr-4" style={{ color: 'var(--text-tertiary)' }}>{impact}</td>
                    <td className="py-2" style={{ color: 'var(--text-tertiary)' }}>{identify}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="not-prose border rounded p-4 my-6" style={{ backgroundColor: 'var(--surface-container-low)', borderColor: '#0078d4' }}>
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-[#0078d4] text-[20px] shrink-0 mt-0.5">
                info
              </span>
              <div className="text-[13px]" style={{ color: '#c7e0f4' }}>
                <strong>Key principle:</strong> Consistent variance across multiple services
                usually indicates a discount program. Inconsistent variance (some services match,
                others do not) suggests a calculator error or misconfiguration — investigate
                immediately.
              </div>
            </div>
          </div>
        </section>

        {/* ── Section 5: Template ── */}
        <section id="template">
          <h2>DIY Reconciliation Template</h2>
          <p>
            Use this template to run your own reconciliations. Copy into a spreadsheet or document
            and fill in for each billing period.
          </p>

          <h3>Reconciliation worksheet</h3>
          <div className="not-prose border rounded p-5 my-4" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
            <table className="w-full text-[13px]">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                  <th className="text-left py-2 pr-4 font-semibold" style={{ color: 'var(--text-tertiary)' }}>Field</th>
                  <th className="text-left py-2 font-semibold" style={{ color: 'var(--text-tertiary)' }}>Value</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Billing period', '__________'],
                  ['Subscription ID', '__________'],
                  ['Discount program (EA/CSP/None)', '__________'],
                  ['Discount rate (if known)', '__________'],
                  ['Test workload description', '__________'],
                  ['', ''],
                  ['Service 1 name', '__________'],
                  ['Service 1 configuration', '__________'],
                  ['Service 1 AzureCalc estimate', '£__________'],
                  ['Service 1 Invoice actual', '£__________'],
                  ['Service 1 Variance %', '__________%'],
                  ['Service 1 Root cause', '__________'],
                  ['', ''],
                  ['Service 2 name', '__________'],
                  ['Service 2 configuration', '__________'],
                  ['Service 2 AzureCalc estimate', '£__________'],
                  ['Service 2 Invoice actual', '£__________'],
                  ['Service 2 Variance %', '__________%'],
                  ['Service 2 Root cause', '__________'],
                  ['', ''],
                  ['Total monthly estimate', '£__________'],
                  ['Total monthly invoice', '£__________'],
                  ['Overall variance %', '__________%'],
                  ['Reconciliation status', 'Pass / Fail'],
                ].map(([field, value]) => (
                  <tr key={field || Math.random()} style={{ borderBottom: '1px solid var(--surface-container-high)' }}>
                    <td className="py-1.5 pr-4 w-48" style={{ color: 'var(--text-tertiary)' }}>{field}</td>
                    <td className="py-1.5" style={{ color: 'var(--text-secondary)' }}>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3>Variance threshold guidance</h3>
          <div className="not-prose flex flex-col gap-2 my-4">
            {[
              {
                threshold: '0% variance',
                meaning: 'Perfect match — calculator is accurate for PAYG rates',
                action: 'Reconciliation passed',
              },
              {
                threshold: '1–5% variance',
                meaning: 'Likely rounding differences or minor free tier usage',
                action: 'Acceptable — document in notes',
              },
              {
                threshold: '15–30% variance',
                meaning: 'Consistent EA/CSP discount rate',
                action: 'Verify discount rate matches variance; reconciliation passed',
              },
              {
                threshold: '40–60% variance on VMs',
                meaning: 'Reserved Instance savings or Azure Hybrid Benefit',
                action: 'Verify RI/AHB application on invoice; reconciliation passed',
              },
              {
                threshold: '>10% variance unexplained',
                meaning: 'Potential calculator error or misconfiguration',
                action: 'Investigate immediately — contact me if AzureCalc.uk appears wrong',
              },
            ].map((item) => (
              <div
                key={item.threshold}
                className="flex gap-4 border rounded p-4"
                style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}
              >
                <div className="flex items-center justify-center w-16 h-8 rounded font-bold text-[12px] shrink-0 text-[#0078d4]" style={{ backgroundColor: 'rgba(0,120,212,0.15)' }}>
                  {item.threshold}
                </div>
                <div>
                  <p className="text-[13px] mb-1" style={{ color: 'var(--text-tertiary)' }}>{item.meaning}</p>
                  <p className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>{item.action}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Section 6: Common Issues ── */}
        <section id="common-issues">
          <h2>Common Reconciliation Issues</h2>

          <div className="not-prose flex flex-col gap-4 my-6">
            {[
              {
                issue: 'Prorated billing confusion',
                symptom: 'Invoice shows 50% of expected cost for a full month',
                cause: 'Resource was provisioned mid-month; billing is accurate',
                fix: 'Check provisioning date in Activity Log; normalize to 30 days for comparison',
              },
              {
                issue: 'Free tier not visible',
                symptom: 'Log Analytics invoice shows less GB than calculator',
                cause: 'First 5GB/day per workspace is free; invoice reflects billable only',
                fix: 'Subtract 5GB × days from calculator input; or use calculator free tier toggle',
              },
              {
                issue: 'Intermittent resource usage',
                symptom: 'VM cost varies wildly month-to-month',
                cause: 'Auto-scaling or stop/start during period',
                fix: 'Use Average or Peak options in calculator; compare range, not single value',
              },
              {
                issue: 'Currency conversion timing',
                symptom: 'Small variance (<2%) on GBP rates',
                cause: 'Azure API updates USD rates; GBP conversion uses daily exchange rate',
                fix: 'Minor variance is expected; document and accept if <2%',
              },
              {
                issue: 'Hidden data processing charges',
                symptom: 'NAT Gateway/Azure Firewall cost higher than expected',
                cause: 'Data processing charges (£0.045/GB for NAT, variable for Firewall)',
                fix: 'Include data volume in calculator; egress adds to hourly base cost',
              },
            ].map((item) => (
              <div
                key={item.issue}
                className="flex gap-4 border rounded p-4"
                style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}
              >
                <span className="material-symbols-outlined text-[#0078d4] text-[22px] shrink-0 mt-0.5">
                  error
                </span>
                <div>
                  <p className="text-[14px] font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>{item.issue}</p>
                  <p className="text-[13px] mb-1" style={{ color: 'var(--text-tertiary)' }}>
                    <strong>Symptom:</strong> {item.symptom}
                  </p>
                  <p className="text-[13px] mb-1" style={{ color: 'var(--text-tertiary)' }}>
                    <strong>Cause:</strong> {item.cause}
                  </p>
                  <p className="text-[13px]" style={{ color: '#a8d8a8' }}>
                    <strong>Fix:</strong> {item.fix}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Section 7: Resources ── */}
        <section id="resources">
          <h2>Official Resources</h2>
          <div className="not-prose flex flex-col gap-2 my-4">
            <ResourceLink
              title="Azure Cost Management + Billing"
              url="https://portal.azure.com/#blade/Microsoft_Azure_CostManagement/Menu/Overview"
              source="Microsoft Azure"
            />
            <ResourceLink
              title="Azure Retail Prices API"
              url="https://learn.microsoft.com/en-us/rest/api/cost-management/retail-prices/azure-retail-prices"
              source="Microsoft Learn"
            />
            <ResourceLink
              title="Enterprise Agreement pricing explained"
              url="https://learn.microsoft.com/en-us/azure/cost-management-billing/manage/ea-pricing-overview"
              source="Microsoft Learn"
            />
            <ResourceLink
              title="Reserved Instance pricing"
              url="https://azure.microsoft.com/en-gb/reserved-instances/"
              source="Microsoft Azure"
            />
          </div>

          <p className="mt-6">
            The{' '}
            <Link to="/methodology" className="text-[#0078d4] hover:underline transition-colors">
              Methodology page
            </Link>{' '}
            explains the Azure Retail Prices API query and how prices are cached in D1. The{' '}
            <Link
              to="/guides/azure-billing-traps-uk"
              className="text-[#0078d4] hover:underline transition-colors"
            >
              Azure Billing Traps guide
            </Link>{' '}
            covers the seven most expensive surprises in Azure pricing.
          </p>

          <div className="not-prose rounded p-4 my-6" style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid rgba(0,120,212,0.4)' }}>
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-[#0078d4] text-[20px] shrink-0 mt-0.5">
                mail
              </span>
              <div className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
                <strong>Found a discrepancy?</strong> If your reconciliation shows unexplained
                variance on PAYG rates, email me at{' '}
                <a
                  href="mailto:gravitycontextdev@gmail.com"
                  className="underline hover:text-[#0078d4]"
                >
                  gravitycontextdev@gmail.com
                </a>{' '}
                with your comparison table. I will verify against the Azure API within 24 hours
                and update the calculator if needed.
              </div>
            </div>
          </div>
        </section>
      </GuideLayout>
    </>
  );
}
